// Shared (client + server) types and mapping for the customer project gallery. Photos live in the
// Cloudinary asset folder `www_viewrail/ugc`, uploaded by the UGC pipeline with contextual metadata
// (caption, alt, product, subsystem, finish, location, project). Sections filter by product; the
// /customer-projects page filters on every facet (see ugc-filters.ts).

// Must list every `product` value the pipeline writes — an unknown value is dropped from a photo.
export const UGC_PRODUCTS = [
  'floating-stairs',
  'cable-railing',
  'rod-railing',
  'glass-railing',
  'stair-treads',
  'handrail',
  'spiral-stairs',
  'traditional',
  'modern-baluster',
  'other',
] as const;
export type UgcProduct = (typeof UGC_PRODUCTS)[number];

// Cable railing system, from the pipeline's `cable_system` context (a photo can show more than one,
// e.g. DriveTite wood posts with a Signature gate). Cable PDPs show only their own system's photos.
export const UGC_CABLE_SYSTEMS = ['signature', 'express', 'drivetite'] as const;
export type UgcCableSystem = (typeof UGC_CABLE_SYSTEMS)[number];

export const isUgcCableSystem = (value: unknown): value is UgcCableSystem =>
  typeof value === 'string' && (UGC_CABLE_SYSTEMS as readonly string[]).includes(value);

export interface UgcPhoto {
  id: string;
  src: string;
  width: number;
  height: number;
  alt: string;
  caption?: string;
  products: UgcProduct[];
  location?: 'interior' | 'exterior';
  /**
   * Every filterable context value by key (product, subsystem, finish, location, and layout/mounting
   * once the pipeline writes them), split on ", ". Raw strings: filter options decide what's valid.
   */
  facets: Record<string, string[]>;
  /**
   * Project numbers, for search only — never rendered. They aren't private (each one is already in
   * the photo's file name and so its URL), but the gallery never displays them.
   */
  projects: string[];
  /** Photographer credit, for professionally shot photos; `url` is their own site. */
  credit?: UgcCredit;
  /**
   * Short, permanent photo code (e.g. VR-7K2M4) shown in the lightbox so a customer and sales can
   * name the exact photo. Unlike the public_id it survives renames and carries no project number.
   */
  code?: string;
}

/**
 * What the browser gets for each photo: enough to draw a tile and the lightbox. Filter metadata and
 * project numbers stay on the server, which does the filtering and search.
 */
export type UgcPhotoCard = Pick<UgcPhoto, 'id' | 'src' | 'width' | 'height' | 'alt' | 'caption' | 'credit' | 'code'>;

export const toPhotoCard = ({ id, src, width, height, alt, caption, credit, code }: UgcPhoto): UgcPhotoCard => ({
  id,
  src,
  width,
  height,
  alt,
  ...(caption ? { caption } : {}),
  ...(credit ? { credit } : {}),
  ...(code ? { code } : {}),
});

// VR- plus five characters from an alphabet without look-alikes (no 0/O, 1/I/L), as the pipeline
// writes them to the `photo_code` context. Anything else in that field is ignored.
export const PHOTO_CODE_PATTERN = /^VR-[2-9A-HJKMNP-Z]{5}$/;

export interface UgcCredit {
  name: string;
  url?: string;
}

const CREDIT_NAME_MAX = 80;

/**
 * Credit metadata is free text from Cloudinary context or the upload form, and the URL ends up in an
 * href, so both are checked here: a name is required, and only an absolute http(s) URL is kept.
 */
export const parseUgcCredit = (name: unknown, url: unknown): UgcCredit | undefined => {
  if (typeof name !== 'string' || !name.trim() || name.length > CREDIT_NAME_MAX) return undefined;
  const credit: UgcCredit = { name: name.trim() };
  if (typeof url === 'string' && url.trim()) {
    try {
      const parsed = new URL(url.trim());
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') credit.url = parsed.toString();
    } catch {
      // An unparseable URL drops the link but keeps the name.
    }
  }
  return credit;
};

/** "studio.example.com" for display next to the name. */
export const creditHost = (url: string) => new URL(url).hostname.replace(/^www\./, '');

export const isUgcProduct = (value: unknown): value is UgcProduct =>
  typeof value === 'string' && (UGC_PRODUCTS as readonly string[]).includes(value);

// Maps a shop category (slug, name or breadcrumb path) to the UGC product it should show. Order
// matters: crumbs read general → specific (Railing / Signature Rod Railing / Posts), so the specific
// systems are tested first and the generic stair rule last. Returns undefined for categories with no
// photo product, and the gallery then hides rather than showing unrelated projects.
export const ugcProductForCategory = (...labels: (string | undefined)[]): UgcProduct | undefined => {
  const text = labels.filter(Boolean).join(' ').toLowerCase();
  if (/rod railing|\brods?\b/.test(text)) return 'rod-railing';
  if (/cable|drive ?tite/.test(text)) return 'cable-railing';
  if (/glass|vedera|talon|standoff|base.?rail/.test(text)) return 'glass-railing';
  if (/tread/.test(text)) return 'stair-treads';
  if (/handrail|top.?cap/.test(text)) return 'handrail';
  if (/flight|floating|stair|mono|stack|cantilever/.test(text)) return 'floating-stairs';
  return undefined;
};

// Maps a cable category path to its system. Express and Signature are tested first: products such as
// "Express DriveTite - Level Cable Railing" sit under an Express or Signature category and show that
// system's photos. Only paths naming neither (DriveTite Wood Components, Wood Posts: …) are DriveTite.
// Generic cable categories (Cable Railing Kits, Tools) return undefined and show every cable photo.
export const ugcCableSystemForCategory = (...labels: (string | undefined)[]): UgcCableSystem | undefined => {
  const text = labels.filter(Boolean).join(' ').toLowerCase();
  if (/express/.test(text)) return 'express';
  if (/signature (cable|drive ?tite)/.test(text)) return 'signature';
  if (/drive ?tite|wood post/.test(text)) return 'drivetite';
  return undefined;
};

/**
 * How strongly a photo is "about" a product, for ordering a product-filtered list: 0 when it's the
 * photo's only product, 1 when it's listed first of several (the pipeline lists the main subject
 * first), 2 when it's a supporting product (a floating stair that happens to have cable railing).
 * Lower sorts first; photos without the product sort last.
 */
export const productRank = (photo: UgcPhoto, product: UgcProduct) => {
  const position = photo.products.indexOf(product);
  if (position < 0) return 3;
  if (photo.products.length === 1) return 0;
  return position === 0 ? 1 : 2;
};

/** Stable sort by productRank, so within a rank the newest-first order from Cloudinary holds. */
export const sortByProductRank = <T extends UgcPhoto>(photos: T[], product: UgcProduct): T[] =>
  photos
    .map((photo, index) => ({ photo, index, rank: productRank(photo, product) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ photo }) => photo);

/**
 * A project / order number as the upload form accepts it (e.g. 610817, AB-123), shared with the
 * library mapping so every accepted number is also searchable.
 */
export const PROJECT_NUMBER_PATTERN = /^[\w-]{1,20}$/;

/** Photos per batch on the /customer-projects page (server first render and each "Load more"). */
export const UGC_PAGE_SIZE = 30;

export const UGC_SUBMIT_PATH = '/share-your-project/';
export const UGC_PAGE_PATH = '/customer-projects/';
/**
 * Terms of Service section on the marketing site that sets out the customer-photo policy (real
 * photos only, nothing AI-generated or AI-edited, how photos are reviewed and credited). The anchor
 * must match the id of that section on the Terms page (#ai-image-use).
 */
export const UGC_PHOTO_POLICY_URL = 'https://www.viewrail.com/terms-of-services#ai-image-use';
/** The curated, professionally photographed gallery on the marketing site. */
export const CURATED_GALLERY_URL = 'https://www.viewrail.com/gallery';
